import React, { useState } from 'react';
import { X, Car, Calendar, MapPin, FileText, Phone, CheckCircle, Loader2 } from 'lucide-react';

/**
 * Modal for customers to report a new damage/accident
 */
const ReportDamageModal = ({ isOpen, onClose, customerToken, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    license_plate: '',
    vehicle_manufacturer: '',
    vehicle_model: '',
    damage_date: '',
    damage_location: '',
    damage_description: '',
    contact_phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.license_plate.trim()) {
      setError('Bitte geben Sie das Kennzeichen ein');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/report-damage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${customerToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(true);
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(data.case_id);
          }, 2000);
        }
      } else {
        setError(data.detail || 'Ein Fehler ist aufgetreten');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      license_plate: '',
      vehicle_manufacturer: '',
      vehicle_model: '',
      damage_date: '',
      damage_location: '',
      damage_description: '',
      contact_phone: ''
    });
    setSuccess(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Schaden gemeldet!</h2>
          <p className="text-gray-600 mb-6">
            Vielen Dank! Wir haben Ihre Schadensmeldung erhalten und werden uns in Kürze bei Ihnen melden, 
            um einen Besichtigungstermin zu vereinbaren.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Sie können den Status im Chat verfolgen.
          </p>
          <button
            onClick={resetAndClose}
            className="w-full py-3 bg-brand-green hover:bg-green-600 text-white rounded-xl font-semibold transition-colors"
          >
            Verstanden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-green to-green-600 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Neuen Schaden melden</h2>
                <p className="text-green-100 text-sm">Wir sind für Sie da!</p>
              </div>
            </div>
            <button
              onClick={resetAndClose}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-5">
            {/* License Plate */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Car className="w-4 h-4 inline mr-2" />
                Kennzeichen *
              </label>
              <input
                type="text"
                name="license_plate"
                value={formData.license_plate}
                onChange={handleChange}
                placeholder="z.B. HH-AB 1234"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green uppercase"
                required
              />
            </div>

            {/* Vehicle */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Hersteller</label>
                <input
                  type="text"
                  name="vehicle_manufacturer"
                  value={formData.vehicle_manufacturer}
                  onChange={handleChange}
                  placeholder="z.B. BMW"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Modell</label>
                <input
                  type="text"
                  name="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={handleChange}
                  placeholder="z.B. 320d"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green"
                />
              </div>
            </div>

            {/* Damage Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Schadensdatum
              </label>
              <input
                type="date"
                name="damage_date"
                value={formData.damage_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green"
              />
            </div>

            {/* Damage Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Schadensort
              </label>
              <input
                type="text"
                name="damage_location"
                value={formData.damage_location}
                onChange={handleChange}
                placeholder="z.B. Hamburg, Hauptstraße"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <FileText className="w-4 h-4 inline mr-2" />
                Kurze Beschreibung
              </label>
              <textarea
                name="damage_description"
                value={formData.damage_description}
                onChange={handleChange}
                placeholder="Was ist passiert? (z.B. Auffahrunfall, Seitenschaden...)"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green resize-none"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Telefon für Rückruf
              </label>
              <input
                type="tel"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="Ihre Telefonnummer"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={resetAndClose}
            className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-3 bg-brand-green hover:bg-green-600 disabled:bg-gray-400 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              'Schaden melden'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportDamageModal;
